import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { LogOut, Save, RotateCcw, ExternalLink, Check } from 'lucide-react';
import LoginView from '../components/admin/LoginView';
import GlobalEditor from '../components/admin/GlobalEditor';
import LangEditor from '../components/admin/LangEditor';
import { API, useSiteConfig } from '../context/SiteConfigContext';
import { useToast } from '../hooks/use-toast';

const TABS = [
  { key: 'global', label: 'Général', hint: 'Circuits, photos, contacts' },
  { key: 'fr', label: '🇫🇷 Français', hint: 'Textes FR' },
  { key: 'en', label: '🇬🇧 English', hint: 'EN texts' },
  { key: 'es', label: '🇪🇸 Español', hint: 'Textos ES' },
  { key: 'de', label: '🇩🇪 Deutsch', hint: 'DE Texte' },
  { key: 'it', label: '🇮🇹 Italiano', hint: 'Testi IT' },
];

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem('exotic_admin_token'));
  const [tab, setTab] = useState('global');
  const [draft, setDraft] = useState(null); // editable copy
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const { reload, setConfig: setLiveConfig } = useSiteConfig();
  const { toast } = useToast();

  const loadDraft = async () => {
    try {
      const { data } = await axios.get(`${API}/site`);
      setDraft(data);
    } catch (e) {
      toast({ title: 'Impossible de charger la configuration', variant: 'destructive' });
    }
  };

  useEffect(() => { if (token) loadDraft(); /* eslint-disable-next-line */ }, [token]);

  const logout = () => { localStorage.removeItem('exotic_admin_token'); setToken(null); };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/site`, draft, { headers: { 'X-Admin-Token': token } });
      setDraft(data);
      setLiveConfig(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      toast({ title: 'Modifications enregistrées' });
      reload();
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
      } else {
        toast({ title: 'Erreur lors de la sauvegarde', variant: 'destructive' });
      }
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    if (!window.confirm('Réinitialiser toute la configuration aux valeurs par défaut ? Les modifications personnalisées seront perdues (photos uploadées conservées).')) return;
    try {
      const { data } = await axios.post(`${API}/admin/site/reset`, {}, { headers: { 'X-Admin-Token': token } });
      setDraft(data);
      setLiveConfig(data);
      toast({ title: 'Configuration réinitialisée' });
      reload();
    } catch {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  if (!token) return <LoginView onLogin={setToken} />;

  if (!draft) {
    return (
      <div className="min-h-screen bg-[#0e2a24] text-white/60 grid place-items-center text-sm">
        Chargement de la configuration…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0e2a24] text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur bg-[#0e2a24]/95 border-b border-white/5">
        <div className="mx-auto max-w-6xl px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex flex-col leading-tight">
            <span className="font-serif text-[15px]">Exotic Tours <span className="text-[#e0b972]">Admin</span></span>
            <span className="text-[11px] text-white/40">Personnalisation complète du site</span>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[12px] rounded-md border border-white/15 hover:border-white/30 px-3 py-2 transition-colors">
              <ExternalLink size={13} /> Voir le site
            </a>
            <button onClick={resetToDefaults} className="inline-flex items-center gap-2 text-[12px] rounded-md border border-white/15 hover:border-white/30 px-3 py-2 transition-colors">
              <RotateCcw size={13} /> Réinitialiser
            </button>
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 text-[12.5px] font-medium rounded-md bg-[#e89142] hover:bg-[#d97e2f] disabled:opacity-70 text-[#1a1a1a] px-4 py-2 transition-colors">
              {saved ? <Check size={14} /> : <Save size={14} />}
              {saving ? 'Sauvegarde…' : (saved ? 'Enregistré' : 'Enregistrer')}
            </button>
            <button onClick={logout} className="inline-flex items-center gap-2 text-[12px] rounded-md bg-white/5 hover:bg-white/10 px-3 py-2 transition-colors">
              <LogOut size={13} /> Déconnexion
            </button>
          </div>
        </div>

        {/* Tabs */}
        <nav className="mx-auto max-w-6xl px-6 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2.5 text-[13px] border-b-2 transition-colors whitespace-nowrap ${
                tab === t.key ? 'border-[#e0b972] text-white' : 'border-transparent text-white/50 hover:text-white/80'
              }`}
            >
              {t.label}
              <span className="ml-2 text-[10px] text-white/30">{t.hint}</span>
            </button>
          ))}
        </nav>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        {tab === 'global' ? (
          <GlobalEditor config={draft} setConfig={setDraft} token={token} />
        ) : (
          <LangEditor lang={tab} config={draft} setConfig={setDraft} />
        )}

        {/* Sticky save bar (mobile-friendly) */}
        <div className="mt-10 flex justify-end">
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 text-[13px] font-medium rounded-md bg-[#e89142] hover:bg-[#d97e2f] disabled:opacity-70 text-[#1a1a1a] px-5 py-2.5 transition-colors">
            {saved ? <Check size={15} /> : <Save size={15} />}
            {saving ? 'Sauvegarde…' : 'Enregistrer les modifications'}
          </button>
        </div>
      </main>
    </div>
  );
};

export default Admin;
